import { useEffect, useRef, useState } from "react";

type EmployeeLike = {
  emp_id?: number | string;
  emp_name?: string;
  role?: string | null;
} | null | undefined;

const defaultsToMyCases = (role?: string | null) => {
  const normalized = String(role || "").trim().toLowerCase();
  return normalized === "designer" || normalized === "senior designer";
};

const employeeKey = (employee: EmployeeLike) =>
  employee?.emp_id == null ? "" : String(employee.emp_id);

export const useMyCasesToggle = (employee: EmployeeLike) => {
  const empId = employeeKey(employee);
  const [enabled, setEnabled] = useState(() =>
    defaultsToMyCases(employee?.role),
  );
  const appliedEmpIdRef = useRef(empId);

  useEffect(() => {
    if (!empId) {
      appliedEmpIdRef.current = "";
      setEnabled(false);
      return;
    }
    // Only apply the role default on login / employee switch — not on refetch.
    if (appliedEmpIdRef.current === empId) return;
    appliedEmpIdRef.current = empId;
    setEnabled(defaultsToMyCases(employee?.role));
  }, [empId, employee?.role]);

  return {
    showToggle: Boolean(employee),
    myCasesOnly: Boolean(empId && enabled),
    enabled,
    setEnabled,
  };
};

const MyCasesToggle = ({
  show,
  enabled,
  empName,
  onChange,
}: {
  show: boolean;
  enabled: boolean;
  empName?: string;
  onChange: (next: boolean) => void;
}) => {
  if (!show) return null;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      title="When on, only cases where you are allocated designer, captain, QC1, QC2, or Designed"
      onClick={() => onChange(!enabled)}
      className="inline-flex items-center gap-2 rounded-xl border border-white/40 bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20 transition"
    >
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
          enabled ? "bg-white" : "bg-white/30"
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full shadow transition-all ${
            enabled ? "left-4 bg-[#2fa9bf]" : "left-0.5 bg-white"
          }`}
        />
      </span>
      <span>My cases{empName ? ` (${empName})` : ""}</span>
    </button>
  );
};

export default MyCasesToggle;
